import { useSelector } from "react-redux";
import store, { setFile } from "../redux";
import { cloneDeep, isEqual } from "lodash";
import { format } from "date-fns";

import { DEFAULT_TARGETING, DEFAULT_FEATURES } from "../components/Remote";

export const fileNameWithDate = (fileName) =>
  `${format(new Date(), "yyyyMMdd-hhmm")}-${fileName}`;

export const stateToFile = () => {
  const file = cloneDeep(store.getState().file);
  file.dtg = format(new Date(), "yyyyMMdd-hhmm");

  // Remove Empty Arrays from Targeting
  file.root.forEach((features, i) => {
    if (isEqual(features.features, DEFAULT_FEATURES)) {
      const shouldContinue = window.confirm(
        `${features.title} has no features, continue?`
      );
      if (!shouldContinue) throw Error("Failed to Save");
    }
    if (isEqual(features.targeting, DEFAULT_TARGETING)) {
      const shouldContinue = window.confirm(
        `${features.title} has no targeting, continue?`
      );
      if (!shouldContinue) throw Error("Failed to Save");
    }
    Object.keys(features.targeting).forEach((key) => {
      // Common case checking each field
      if (
        Array.isArray(file.root[i].targeting[key]) &&
        !file.root[i].targeting[key].length
      ) {
        delete file.root[i].targeting[key];
      }

      // Case for checking bandwidth field
      if (!file.root[i].targeting.bandwidth) {
        delete file.root[i].targeting.bandwidth;
      }

      // Formatting timezone field array of strings to array of numbers
      if (
        file.root[i].targeting.timezone?.length &&
        file.root[i].targeting.timezone[0].indexOf("GMT") !== -1
      ) {
        const timeZoneRawList = file.root[i].targeting.timezone;

        // Creating an set with unique numeric values ​​from string values ​​of timezones
        const timeZoneList = new Set(
          timeZoneRawList.map((item) => {
            const splittedTimeZone = item.match(/\+?-?\d+/g);

            if (Math.abs(Number(splittedTimeZone[0])) > 9) {
              return splittedTimeZone[0] + "00";
            } else {
              return (
                splittedTimeZone[0].slice(0, 1) +
                "0" +
                splittedTimeZone[0].slice(1) +
                "00"
              );
            }
          })
        );
        delete file.root[i].targeting.timezone;
        file.root[i].targeting.timezone = Array.from(timeZoneList);
      }

      if (file.root[i].targeting.dma) {
        file.root[i].targeting.dma = file.root[i].targeting.dma.filter(
          (item) => item !== undefined
        );
      }

      if (file.root[i].targeting.zip) {
        file.root[i].targeting.zip = file.root[i].targeting.zip.filter(
          (item) => item !== undefined
        );
      }
    });
  });

  // Stringify and add Serial Numbers
  return JSON.stringify(file, null, 2);
};

export const listenToDragDrop = () => {
  if (window.File && window.FileReader && window.FileList && window.Blob) {
    // Great success!
    function handleJSONDrop(evt) {
      evt.stopPropagation();
      evt.preventDefault();
      const f = evt.dataTransfer.files[0];
      if (!f.type.match("application/json")) {
        return;
      }

      const reader = new FileReader();

      // Closure to capture the file information.
      reader.onload = (function (theFile) {
        return function (e) {
          fileToState(JSON.parse(e.target.result));
        };
      })(f);

      reader.readAsText(f);
    }

    function handleDragOver(evt) {
      evt.stopPropagation();
      evt.preventDefault();
      evt.dataTransfer.dropEffect = "copy"; // Explicitly show this is a copy.
    }

    // Setup the dnd listeners.
    var dropZone = document.getElementsByTagName("html")[0];
    dropZone.addEventListener("dragover", handleDragOver, false);
    dropZone.addEventListener("drop", handleJSONDrop, false);
  } else {
    alert("The File APIs are not fully supported in this browser.");
  }
};

export function fileToState(file) {
  file.root = file.root.map((feature) => {
    return {
      ...feature,
      targeting: {
        models: [],
        internet: [],
        environment: [],
        swid: [],
        times: [],
        state: [],
        rxid: [],
        timezone: [],
        bandwidth: 0,
        dma: [],
        zip: [],
        ...feature.targeting,
      },
    };
  });

  if (store.getState().file.root.length) {
    const overwrite = window.confirm(
      "Are you sure you want to overwrite the current features?"
    );
    if (!overwrite) {
      return;
    }
  }

  store.dispatch(setFile(file));
}

export function downloadJson(fileName) {
  try {
    const file = stateToFile();
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(file);
    const el = document.createElement("a");
    el.setAttribute("href", dataStr);
    el.setAttribute("download", `${fileNameWithDate(fileName)}.json`);
    el.click();
  } catch (e) {
    console.error(e);
    return;
  }
}

export async function downloadTgz(fileName, resp) {
  try {
    const { data, headers } = await resp;

    const downloadUrl = window.URL.createObjectURL(
      new Blob([data], { type: headers["content-type"] })
    );
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.setAttribute("download", `${fileNameWithDate(fileName)}.tgz`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (e) {
    console.error(e);
    return;
  }
}

export const useIsReady = () => {
  return useSelector(({ file: { features }, file }) => {
    return features.reduce((curr, features) => {
      if (features.feature.length) {
        return curr;
      } else {
        return false;
      }
    }, true);
  });
};

export const dateToGMTepoch = (date) => {
  return Math.floor(
    (date.getTime() - date.getTimezoneOffset() * 60000) * 0.001
  );
};

export const GMTepochtoDate = (epoch) => {
  return new Date(epoch * 1000 + new Date().getTimezoneOffset() * 60000);
};
